/**
 * <purchase-options>
 *
 * Custom Subscribe / One-time + frequency selector (Quick View), where there is no
 * Recharge widget. It is the source of truth for the purchase choice and:
 *   1. drives the shared <subscription-selector> via setSellingPlan() (cart side), and
 *   2. emits `purchase-option:change` so the headline price (<subscription-price>) and
 *      the [data-hide-on-onetime] blocks (refill) sync without the widget.
 *
 * Per-variant plan/price data is rendered by Liquid (.purchase-options-data) so the
 * pills + prices remap correctly on a variant (colour) change.
 */
if (!customElements.get('purchase-options')) {
  customElements.define(
    'purchase-options',
    class PurchaseOptions extends HTMLElement {
      connectedCallback() {
        this.productId = this.dataset.productId;
        try {
          this.data = JSON.parse(this.querySelector('.purchase-options-data')?.textContent || '{}');
        } catch (e) {
          this.data = {};
        }

        this.subscribeCard = this.querySelector('.purchase-option--subscribe');
        this.onetimeCard = this.querySelector('.purchase-option--onetime');
        this.pillsWrap = this.querySelector('.purchase-option__pills');

        this.addEventListener('change', (e) => {
          if (e.target.name && e.target.name.indexOf('PurchaseOption-') === 0) this.onTypeChange();
        });
        this.addEventListener('click', (e) => {
          const pill = e.target.closest('.purchase-option__pill');
          if (pill) {
            e.preventDefault();
            this.onPillChange(pill);
          }
        });

        this._onVariant = () => this.onVariantChange();
        document.addEventListener('variant:changed', this._onVariant);

        // Initial sync (default = subscribe + first frequency). Deferred to the next frame so
        // <subscription-price> and the hide-on-onetime listeners — which may upgrade in the same
        // insert batch as this element — are attached before we emit `purchase-option:change`.
        requestAnimationFrame(() => this.apply());
      }

      disconnectedCallback() {
        document.removeEventListener('variant:changed', this._onVariant);
      }

      get scope() {
        return this.closest('product-info') || document;
      }

      get selector() {
        return (
          this.scope.querySelector(`subscription-selector[data-product-id="${this.productId}"]`) ||
          this.scope.querySelector('subscription-selector')
        );
      }

      get isSubscribe() {
        const r = this.querySelector('input[value="subscribe"]');
        return r ? r.checked : true;
      }

      get activePill() {
        return (
          this.pillsWrap?.querySelector('.purchase-option__pill.is-active') ||
          this.pillsWrap?.querySelector('.purchase-option__pill')
        );
      }

      currentVariantId() {
        const input =
          this.scope.querySelector('form[is="product-form"] [name="id"]') ||
          this.scope.querySelector('[name="id"]');
        return input ? String(input.value) : Object.keys(this.data)[0] || null;
      }

      onTypeChange() {
        const subscribe = this.isSubscribe;
        this.subscribeCard?.classList.toggle('is-active', subscribe);
        this.onetimeCard?.classList.toggle('is-active', !subscribe);
        this.apply();
      }

      onPillChange(pill) {
        this.pillsWrap.querySelectorAll('.purchase-option__pill').forEach((p) => {
          p.classList.toggle('is-active', p === pill);
        });
        // Choosing a frequency implies Subscribe.
        const r = this.querySelector('input[value="subscribe"]');
        if (r && !r.checked) {
          r.checked = true;
          this.onTypeChange();
          return;
        }
        this.apply();
      }

      onVariantChange() {
        const vid = this.currentVariantId();
        const entry = vid ? this.data[vid] : null;
        if (entry && entry.plans && this.pillsWrap) {
          const pills = [...this.pillsWrap.querySelectorAll('.purchase-option__pill')];
          pills.forEach((pill, i) => {
            const plan = entry.plans[i];
            if (plan) {
              pill.dataset.planId = plan.id;
              pill.dataset.price = plan.price;
            }
          });
        }
        this.apply();
      }

      apply() {
        const vid = this.currentVariantId();
        const entry = vid ? this.data[vid] : null;
        const hasSub = Boolean(entry && entry.plans && entry.plans.length);
        const subscribe = this.isSubscribe && hasSub;
        const pill = this.activePill;
        const planId = subscribe && pill ? pill.dataset.planId : null;

        // 1) Cart side — the shared selector owns the hidden selling_plan input.
        this.selector?.setSellingPlan(planId);

        // 2) Our own subscribe-card price (per active frequency) + Save badge (per variant).
        const subEl = this.querySelector('[data-sub-price]');
        if (subEl && pill?.dataset.price) subEl.textContent = pill.dataset.price;
        const onetimeEl = this.querySelector('[data-onetime-price]');
        if (onetimeEl && entry?.onetime) onetimeEl.textContent = entry.onetime;
        const saveEl = this.querySelector('[data-save-badge]');
        if (saveEl) {
          if (entry && entry.save) {
            saveEl.textContent = 'Save ' + entry.save;
            saveEl.hidden = false;
          } else {
            saveEl.hidden = true;
          }
        }

        // 3) Retain the latest state globally so a late-upgrading <subscription-price> or
        // hide-on-onetime listener can read it on connect (avoids the initial-load race).
        window.__purchaseOptionState = window.__purchaseOptionState || {};
        window.__purchaseOptionState[this.productId] = { isSubscription: subscribe, sellingPlanId: planId };

        // 4) Signal the headline price + hide-on-onetime blocks (no Recharge widget here).
        document.dispatchEvent(
          new CustomEvent('purchase-option:change', {
            bubbles: true,
            detail: { productId: this.productId, isSubscription: subscribe, sellingPlanId: planId },
          })
        );
      }
    }
  );
}
